import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogicContext from "../../Context/LogicContext";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import { debounce } from "lodash";
import { customerService } from "../../services/customer.service";
import { driverAssignService } from "../../services/driverAssignService";
import { SetAppDriverList, GetAppDriverList } from "../../store/driverListAtom";
import pencil from "../../assets/pencil.png";

export const UserDeliveryAppAddNew = () => {
  const logicContext = useContext(LogicContext);
  const myPromiseModal = createModal(GeneralModal);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const setAppDriverListData = SetAppDriverList();
  const getAppDriverListData = GetAppDriverList();

  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const searchRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced Search
  const debouncedSearch = useRef(
    debounce((search) => {
      if (search.length < 3) {
        setSearchResult([]);
        setShowDropdown(false);
        return;
      }

      setLoading(true);
      customerService.getCustomerBySearch(search)
        .then((res) => {
          const results = res?.data || [];
          setSearchResult(results);
          setShowDropdown(results.length > 0);
        })
        .catch(() => {
          setSearchResult([]);
          setShowDropdown(false);
        })
        .finally(() => setLoading(false));
    }, 500)
  ).current;

  const handleUserSelect = (user) => {
    setSearchTerm(user.Name || user.Customer_Name || "");
    setSelectedUser(user);
    setShowDropdown(false);
  };

  const handleSave = async () => {
    if (!selectedUser) {
      logicContext.showToast("Please select a user first", "error");
      return;
    }

    const confirm = await myPromiseModal({
      title: "Confirm Assignment",
      body: `You are about to assign ${selectedUser.Name || selectedUser.Customer_Name} to the User Delivery App. Continue?`,
      showInput: false,
    });

    if (!confirm?.decision) return;

    const payload = {
      delivery_user_id: selectedUser.Customer_Id || selectedUser.User_Id,
      option: 1
      // Add other fields as required by the backend API later
    };

    try {
      console.log('payload ', payload);
      const res = await driverAssignService.submitDeliveryAppUser(payload);
      const response = logicContext.httpResponseManager(res.data?.[0] || res.data?.result_status?.[0]);

      if (response.message_id === 1) {
        logicContext.showToast("User assigned successfully!", "success");
        // Optionally refresh the list or navigate back
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}`);
        // setSearchResult([]);
        // setSearchTerm("");
        // setSelectedUser(null);
      } else {
        logicContext.showToast(response.message_text || "Failed to assign user", "error");
      }
    } catch (err) {
      console.error(err);
      logicContext.showToast("An error occurred while saving", "error");
    }
  };

  return (
    <section className="mt-6 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
        {/* Header Banner */}
        <div className="h-40 bg-gradient-to-r from-blue-700 to-blue-600 flex items-center px-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Assign New Driver</h1>
            <p className="text-blue-100 font-medium">Search for a system user to grant delivery app access.</p>
          </div>
        </div>

        <div className="p-8 -mt-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-50">
            {/* Search Section */}
            <div className="relative mb-8" ref={searchRef}>
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                Find User / Driver
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type name, email, or phone number (min. 3 chars)..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                  onFocus={() => searchResult.length > 0 && setShowDropdown(true)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-gray-800 shadow-sm"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl">
                  <i className="fas fa-search"></i>
                </div>
                {loading && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>

              {/* Search Dropdown */}
              {showDropdown && (
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto">
                  {searchResult.length > 0 ? (
                    searchResult.map((user, index) => (
                      <div
                        key={user.Customer_Id || user.User_Id || index}
                        onClick={() => handleUserSelect(user)}
                        className="px-6 py-4 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            {(user.Name || user.Customer_Name)?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-gray-800">{user.Name || user.Customer_Name}</div>
                            <div className="text-sm text-gray-500 flex items-center gap-2">
                              <span>{user.Email_Address || user.Email}</span>
                              {user.Mobile && (
                                <>
                                  <span className="text-gray-300">|</span>
                                  <span>{user.Mobile}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : !loading && (
                    <div className="p-8 text-center text-gray-400 font-medium">
                      No users found matching "{searchTerm}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected User Details Preview */}
            {selectedUser && (
              <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-6 mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
                <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-4">Selection Preview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Full Name</p>
                    <p className="font-bold text-gray-800 text-lg">{selectedUser.Name || selectedUser.Customer_Name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Customer/User ID</p>
                    <p className="font-bold text-gray-800">{selectedUser.Customer_Id || selectedUser.User_Id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Email Address</p>
                    <p className="font-semibold text-gray-700">{selectedUser.Email_Address || selectedUser.Email || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Phone Number</p>
                    <p className="font-semibold text-gray-700">{selectedUser.Mobile || "—"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between border-t border-gray-100 pt-8 mt-4">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 text-gray-500 font-bold hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedUser}
                className={`flex items-center gap-3 px-10 py-4 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 transition-all font-bold tracking-wide ${!selectedUser ? "opacity-50 cursor-not-allowed grayscale" : "hover:scale-105 active:scale-95"
                  }`}
              >
                <img src={pencil} alt="Assign" className="w-5 h-5 brightness-0 invert" />
                CONFIRM ASSIGNMENT
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserDeliveryAppAddNew;